import convert from "convert-units";
import cloneDeep from "lodash/cloneDeep";
import lround from "lodash/round";
import React from "react";
import { withTranslation } from "react-i18next";
import submit from "submit-form";
import { getExerciseName } from "../../../../parts/modals/learn-more/blockHelpers";
import { mapParameter } from "../../../../parts/other-exercise/CustomParametersSets";
import SvgIcon from "../../../../parts/SvgIcon";
import { RillContext } from "../../../../rill/RillReact/share/base";
import { isNotMac } from "../../../../utils/browser";
import fromIntensity from "../../../../utils/get-zone";
import {
  buildSupersets,
  calculatePrefixForExercise,
} from "../../../../utils/prefix-calculator";
import NotesWrapper from "../../../dashboard/program-builder/components/NotesWrapper";
import WorkoutParamsRow from "../../calendar/components/EditMode/ExerciseEditor/WorkoutParamsRow";
import EditorExerciseLineItem from "./EditorExerciseLineItem";
import PbExerciseOptions from "./PbExerciseOptions";

class EditorSessionTableRow extends React.Component {
  constructor(props, ctx) {
    super(props, ctx);
    this.state = {
      currentRow: 0,
      isAddingSet: false,
      sets: 0,
      reps: 0,
      rpe: "",
      load: 0,
      percentRepMax: 0,
      addingNote: false,
    };

    // Used to change its value and trigger its onChange from this component
    this.notesInputRef = React.createRef();

    this.localLoadInputs = [];
    this.localIntensityInputs = [];

    this._handleOpenAddSet = this._handleOpenAddSet.bind(this);
    this._handleCloseAddSet = this._handleCloseAddSet.bind(this);
    this._handleNewSetChange = this._handleNewSetChange.bind(this);
    this._handleSaveNewSet = this._handleSaveNewSet.bind(this);
    this._handleNewMovementChange = this._handleNewMovementChange.bind(this);
    this._checkIfEditedExercise = this._checkIfEditedExercise.bind(this);
    this.handleDeleteSetInState = this.handleDeleteSetInState.bind(this);
    this._handleSaveNewSetForOther = this._handleSaveNewSetForOther.bind(this);
    this._handleCreateSuperset = this._handleCreateSuperset.bind(this);
    this._handleRemoveSuperset = this._handleRemoveSuperset.bind(this);
    this._onAddNote = this._onAddNote.bind(this);
  }

  // Add Set functions
  // handles the opening of the Add set line
  _handleOpenAddSet(e, setIndex) {
    e.preventDefault();
    this.setState({ isAddingSet: true, currentRow: setIndex });
  }
  // handles the closing of the Add set line
  // if the add set line is closed, we should reset all the states
  _handleCloseAddSet(e) {
    e.preventDefault();
    this.setState({
      isAddingSet: false,
      currentRow: 0,
      sets: 0,
      reps: 0,
      rpe: "",
      load: 0,
      percentRepMax: 0,
    });
  }
  // handles any changes for the fields in the add set line
  _handleNewSetChange({ target: { name, value } }, defaultRepMax) {
    const { exercises, exercise, fromMetric } = this.props;
    const { units } = this.context.locals.activeUser;
    const activeExercise = exercises.find((elem) => {
      return elem._id === exercise;
    });
    const isAssistant = activeExercise.type === "assistant";
    const { secondaryRepMaximum } = activeExercise;
    const repMax =
      activeExercise.type === "primary"
        ? defaultRepMax
        : Math.round(fromMetric(secondaryRepMaximum, units), 0);

    // if the name is load and an assistant exercise, ensure conversion is alright
    if (name === "load" && isAssistant) {
      // if the units are imperial convert
      if (units === "imperial") {
        this.setState({
          percentRepMax: 0,
          load: lround(convert(value).from("lb").to("kg"), 2),
        });
      }
      // otherwise, simply return the value
      else {
        this.setState({ percentRepMax: 0, load: value });
      }
    }
    // else if the field is load or pRM, calculate and set states.
    else if ((name === "load" || name === "percentRepMax") && !isAssistant) {
      // if name is load
      if (name === "load") {
        this.setState({
          percentRepMax: lround((value / repMax) * 100, 1),
          load: value,
        });
      }
      // else if name is percentRepMax
      else if (name === "percentRepMax") {
        this.setState({
          load: lround((repMax * value) / 100, 2),
          percentRepMax: value,
        });
      }
      // recalculating the fields to be shown.
      const localLoadInput = this.localLoadInputs.new;
      if (localLoadInput) {
        if (name === "percentRepMax") {
          localLoadInput.value = lround((repMax * value) / 100, 2);
        } else {
          this.localIntensityInputs.new.value = lround(
            (value / repMax) * 100,
            1
          );
        }
      }
    }
    // else set state dynamically
    else {
      this.setState({ [name]: value });
    }
  }

  validateSaveNewSet(newSet, isAssistant, defaultRepMax) {
    if (
      newSet.sets <= 0 ||
      newSet.sets > 500 ||
      (!isAssistant && defaultRepMax <= 0) ||
      (newSet.rpe !== "AMRAP" && newSet.reps <= 0)
    )
      return false;

    return true;
  }

  _checkIfEditedExercise() {
    return this.props.exercisesInState.findIndex(
      (exercise) => exercise._id === this.props.exercise
    );
  }
  // submits the newly entered set into the exercise
  _handleSaveNewSet(e, isEquipped, defaultRepMax, _activeUser, exId, setIndex) {
    e.preventDefault();
    const { sets, reps, rpe, load, percentRepMax, repsTwo, repsThree } =
      this.state;
    // Note: activeUser is not the same as the activeUser in the context
    // activeUser is the user or athlete editing or being edited.
    // this.context.locals.activeUser is the user currently Signed on.
    const { units } = this.context.locals.activeUser;
    const isRepRange = String(reps).includes("-");
    const repsRange = isRepRange ? reps.split("-") : reps;
    const newSet = {
      sets,
      reps: isRepRange ? repsRange[1] || repsRange[0] : reps,
      percentRepMax,
      load:
        units === "imperial"
          ? lround(convert(load).from("lb").to("kg"), 2)
          : load,
      equipped: isEquipped,
      zone: fromIntensity(percentRepMax, reps),
      rpe,
      repsLower: isRepRange && repsRange[1] ? repsRange[0] : null,
    };

    const exerciseIndex = this.props.exercises.findIndex(
      (elem) => elem._id === exId
    );

    const copyExercise = cloneDeep(this.props.exercises[exerciseIndex]);
    const isAssistant = copyExercise.type === "assistant";
    const editedExerciseIndex = this._checkIfEditedExercise();

    let complexExercises = [];
    if (copyExercise.complex && copyExercise.complexInstance) {
      complexExercises = (
        this.props.ungroupedExercises || this.props.exercises
      ).filter(
        (ex) =>
          ex.complexInstance === copyExercise.complexInstance &&
          ex.complex === copyExercise.complex &&
          ex._id !== exId
      );
    }

    if (
      this.props.isProgramEditor &&
      this.validateSaveNewSet(newSet, isAssistant, defaultRepMax)
    ) {
      // optimistic update
      copyExercise.sets.splice(parseInt(setIndex, 10) + 1, 0, newSet);
      if (complexExercises?.length > 0) {
        const updatedExercises = [copyExercise];
        complexExercises.forEach((ex) => {
          const copyEx = cloneDeep(ex);
          const copyNewSet = cloneDeep(newSet);
          if (ex.indexInComplex === 1) copyNewSet.reps = repsTwo || reps;
          if (ex.indexInComplex === 2) copyNewSet.reps = repsThree || reps;
          copyEx.sets.splice(parseInt(setIndex, 10) + 1, 0, copyNewSet);
          updatedExercises.push(copyEx);
        });
        this.props.updateExercises(updatedExercises);
      } else {
        this.props.updateExercise(copyExercise);
      }

      if (editedExerciseIndex > -1) {
        // update exercise in state (the one send to the api if clicking on save)
        const copyExerciseEdited = cloneDeep(
          this.props.exercisesInState[editedExerciseIndex]
        );
        copyExerciseEdited.sets.splice(parseInt(setIndex, 10) + 1, 0, newSet);
        this.props.updateExerciseByState(
          copyExerciseEdited,
          editedExerciseIndex
        );
      }
    }

    if (editedExerciseIndex < 0) {
      // Not yet tracked in the quick-edit local state: add it there instead of
      // POSTing straight to the server (see handleAddSetLocally for why).
      this.props.handleAddSetLocally(
        exId,
        setIndex,
        newSet,
        complexExercises.map((ex) => ex._id),
        repsTwo,
        repsThree
      );
    }

    this._handleCloseAddSet(e);
  }

  handleDeleteSetInState(setIndex) {
    const editedExerciseIndex = this._checkIfEditedExercise();
    if (editedExerciseIndex > -1) {
      // update exercise in state (the one send to the api if clicking on save)
      const copyExerciseEdited = cloneDeep(
        this.props.exercisesInState[editedExerciseIndex]
      );
      copyExerciseEdited.sets.splice(setIndex, 1);
      this.props.updateExerciseByState(copyExerciseEdited, editedExerciseIndex);
    }
  }

  _handleNewMovementChange(value, exId, setIndex) {
    const { handleQuickEditChange } = this.props;

    if (value) {
      const e = {
        target: { name: "exerciseType", value },
      };

      handleQuickEditChange(e, exId, setIndex);
    }
  }
  _handleSaveNewSetForOther(e, exId, setIndex, _activeUser, activeExercise) {
    e.preventDefault();

    let newSet = {};
    activeExercise.parameters.forEach((elem) => {
      newSet[mapParameter(elem)] = this.state[mapParameter(elem)];
    });
    const isRepRange = String(newSet.reps).includes("-");
    if (newSet.reps) {
      const repsRange = isRepRange ? newSet.reps.split("-") : newSet.reps;
      newSet = {
        ...newSet,
        reps: isRepRange ? repsRange[1] || repsRange[0] : newSet.reps,
        repsLower: isRepRange && repsRange[1] ? repsRange[0] : null,
      };
    }
    const exerciseIndex = this.props.exercises.findIndex(
      (elem) => elem._id === exId
    );
    const copyExercise = cloneDeep(this.props.exercises[exerciseIndex]);
    const editedExerciseIndex = this._checkIfEditedExercise();

    if (this.props.isProgramEditor) {
      // optimistic update
      copyExercise.sets.splice(parseInt(setIndex, 10) + 1, 0, newSet);
      this.props.updateExercise(copyExercise);

      if (editedExerciseIndex > -1) {
        // update exercise in state (the one send to the api if clicking on save)
        const copyExerciseEdited = cloneDeep(
          this.props.exercisesInState[editedExerciseIndex]
        );
        copyExerciseEdited.sets.splice(parseInt(setIndex, 10) + 1, 0, newSet);
        this.props.updateExerciseByState(
          copyExerciseEdited,
          editedExerciseIndex
        );
      }
    }

    if (editedExerciseIndex < 0) {
      // Not yet tracked in the quick-edit local state: add it there instead of
      // POSTing straight to the server (see handleAddSetLocally for why).
      this.props.handleAddSetLocally(exId, setIndex, newSet);
    }

    this._handleCloseAddSet(e);
  }

  _handleCreateSuperset(fullExercise, exercise, nextExercise) {
    submit(`/actions/exercise/superset/add`, {
      method: "POST",
      body: {
        exercise,
        nextExercise,
        fullExercise,
      },
    });
    if (typeof this.props.setEditing === "function") {
      this.props.setEditing();
    }
  }

  _handleRemoveSuperset(fullExercise, exercise) {
    submit(`/actions/exercise/superset/remove`, {
      method: "POST",
      body: {
        exercise,
        fullExercise,
      },
    });
    if (typeof this.props.setEditing === "function") {
      this.props.setEditing();
    }
  }

  _onAddNote() {
    this.setState({ addingNote: true });
  }

  render() {
    const { withWeight } = this.context.locals;
    const { t } = this.props;
    const {
      exercises = [],
      exercise,
      fromMetric,
      activeUser,
      isProgramEditor,
      openQuickEditMode,
      handleQuickEditChange,
      loadInputs,
      intensityInputs,
      isQuickEditOpen,
      isSectionLast,
      session,
      sessionIndex,
      updateExercise,
      deleteExercise,
      stateExercises,
      isCoach,
      readOnly,
      quickEditSessionId,
    } = this.props;
    const { isAddingSet, currentRow, load, sets, rpe, reps, percentRepMax } =
      this.state;
    const newSet = {
      sets,
      load,
      rpe,
      reps,
      percentRepMax,
    };

    const activeExercise = exercises.find((ex) => ex._id === exercise);

    // Need the state exercises so we can update the notes state
    const activeStateExercise =
      stateExercises.find((ex) => ex._id === exercise) || activeExercise;

    // A movement picked from the inline dropdown (see
    // EditorSessionTable.handleQuickEditChange's "exerciseType" branch) is
    // buffered into stateExercises, not persisted immediately - reflect the
    // pick in the dropdown's collapsed display so it doesn't look like the
    // pick didn't register while it waits for Save. Derived from
    // stateExercises (already correctly cleared on Discard/Save) instead of
    // separate local state, so there's nothing to go stale across sessions.
    const hasPendingMovement =
      activeStateExercise !== activeExercise &&
      (activeStateExercise.type !== activeExercise.type ||
        activeStateExercise.primary !== activeExercise.primary ||
        activeStateExercise.secondary !== activeExercise.secondary);
    const newMovement = hasPendingMovement
      ? getExerciseName(activeStateExercise)
      : "";

    const supersets = buildSupersets(exercises);
    const activeExerciseIndex = exercises.findIndex(
      (ex) => ex._id === activeExercise._id
    );

    // Have to trim the notes cause in some cases comes as " " which is set
    // like that to avoid rill from getting rid of it, but still counts as an empty note
    const notes = activeStateExercise?.notes;
    const exHasNotes =
      notes === 0 || notes === "0"
        ? false
        : !!Number(notes) || notes?.trim().length > 0;

    if (activeExercise?.sets) {
      const isLast = activeExerciseIndex === exercises.length - 1;
      const nextExercise = !isLast && exercises[activeExerciseIndex + 1];
      const isSuperset = activeExercise.nextSuperset === nextExercise._id;
      const isNotMacValue = isNotMac();
      const isComplex = !!activeExercise.complex;
      const nextIsComplex = !!nextExercise?.complex;

      const onInputHandleQuickEditChange = (e) =>
        handleQuickEditChange(e, activeExercise._id, null);

      const handleWorkoutParamsInput = ({ value, field }) =>
        handleQuickEditChange(
          { target: { name: field, value } },
          activeExercise._id,
          null
        );

      const onClickOpenQuickEditMode = (e) => {
        if (!readOnly && !e.target.closest("[data-workout-params]")) {
          openQuickEditMode(e);
        }
      };

      let prefix = "";
      if (activeExerciseIndex >= 0) {
        prefix =
          this.props.sectionPrefix ??
          calculatePrefixForExercise(activeExercise._id, supersets);
      }

      const OrderArrowsComponent = () => (
        <img
          src="/img/new-icons/drag-outline.svg"
          className="grabber cursor-grab drag-icon"
          title={t("athlete-portal:exerciseEditor.dragMoveExercise")}
          alt={t("athlete-portal:exerciseEditor.dragMoveExercise")}
        />
      );

      const OptionsComponent = () => (
        <PbExerciseOptions
          activeExercise={activeExercise}
          week={session.week}
          session={sessionIndex + 1}
        />
      );

      return (
        <li
          className={`Program-Editor-Exercise ${this.props.altClass || ""} ${
            isNotMacValue ? "padding-scroll-no-mac" : ""
          } ${isSuperset ? "is-superset" : ""} ${readOnly ? "read-only" : ""}`}
          data-id={activeExercise._id}
          id={activeExercise._id}
          key={activeExercise._id}
          onClick={onClickOpenQuickEditMode}
        >
          {activeExercise.sets.map((set, setIndex) => {
            return (
              <EditorExerciseLineItem
                key={`${activeExercise._id}${setIndex}`}
                handleQuickEditChange={handleQuickEditChange}
                _handleOpenAddSet={this._handleOpenAddSet}
                _handleCloseAddSet={this._handleCloseAddSet}
                _handleNewSetChange={this._handleNewSetChange}
                _handleSaveNewSet={this._handleSaveNewSet}
                _handleNewMovementChange={this._handleNewMovementChange}
                set={set}
                newSet={newSet}
                setIndex={setIndex}
                activeExercise={activeExercise}
                isProgramEditor={isProgramEditor}
                fromMetric={fromMetric}
                activeUser={activeUser}
                withWeight={withWeight}
                loadInputs={loadInputs}
                intensityInputs={intensityInputs}
                localLoadInputs={this.localLoadInputs}
                localIntensityInputs={this.localIntensityInputs}
                isQuickEditOpen={isQuickEditOpen}
                isAddingSet={isAddingSet}
                currentRow={currentRow}
                units={this.context.locals.activeUser.units}
                newMovement={newMovement}
                setLength={activeExercise.sets.length}
                activeContextUser={this.context.locals.activeUser}
                activeUserToRequest={this.context.locals.activeUserToRequest}
                queryEditedExercise={this.context.locals.query.editExerciseId}
                updateExercise={updateExercise}
                deleteExercise={deleteExercise}
                handleDeleteSetInState={this.handleDeleteSetInState}
                _handleSaveNewSetForOther={this._handleSaveNewSetForOther}
                OrderArrowsComponent={OrderArrowsComponent}
                prefix={prefix}
                OptionsComponent={OptionsComponent}
                isCoach={isCoach}
                activeUserToHandleCopyAction={
                  this.context.locals.athlete ||
                  this.context.locals.activeUserToRequest
                }
                readOnly={readOnly}
                quickEditSessionId={quickEditSessionId}
              />
            );
          })}
          <div data-workout-params>
            <WorkoutParamsRow
              exercise={activeExercise}
              newExercise={activeStateExercise}
              editDayMode={isQuickEditOpen}
              editable={true}
              handleInput={handleWorkoutParamsInput}
              exerciseId={activeExercise._id}
            />
          </div>
          {(exHasNotes || this.state.addingNote) && (
            <NotesWrapper
              isProgramEditor={isProgramEditor}
              openQuickEditMode={isQuickEditOpen}
              onInputHandleQuickEditChange={onInputHandleQuickEditChange}
              activeExercise={activeExercise}
              notesInputRef={this.notesInputRef}
            />
          )}

          <div role="supersets-notes-buttons">
            {isQuickEditOpen &&
              nextExercise &&
              !isLast &&
              !isSectionLast &&
              !isSuperset &&
              !isComplex &&
              !nextIsComplex && (
                <button
                  data-button-type="text"
                  className="superset-button"
                  onClick={(e) => {
                    e.stopPropagation();
                    e.preventDefault();
                    this._handleCreateSuperset(
                      activeExercise,
                      activeExercise._id,
                      nextExercise._id
                    );
                  }}
                  type="button"
                >
                  <img
                    src="/img/new-icons/superset-gradient.svg"
                    height={16}
                    width={16}
                    alt="superset"
                  />
                  <span>{t("athlete-portal:programs.superset")}</span>
                </button>
              )}

            {isQuickEditOpen && nextExercise && !isLast && isSuperset && (
              <button
                data-button-type="text"
                className="superset-button"
                onClick={(e) => {
                  e.stopPropagation();
                  e.preventDefault();
                  this._handleRemoveSuperset(
                    activeExercise,
                    activeExercise._id,
                    nextExercise._id
                  );
                }}
                type="button"
              >
                <img
                  src="/img/new-icons/superset-danger.svg"
                  height={16}
                  width={16}
                  alt="remove superset"
                />
                <span style={{ background: "none", color: "var(--danger)" }}>
                  {t("athlete-portal:programs.removeSuperset")}
                </span>
              </button>
            )}

            {!exHasNotes && isQuickEditOpen && !this.state.addingNote && (
              <button
                data-button-type="text"
                onClick={() => {
                  this._onAddNote();
                }}
                type="button"
              >
                <SvgIcon
                  src="/img/new-icons/note-gradient.svg"
                  alt="add notes"
                />
                <span>{t("athlete-portal:programs.addNote")}</span>
              </button>
            )}

            {exHasNotes && isQuickEditOpen && (
              <button
                data-button-type="text-white"
                data-action="cancel"
                onClick={() => {
                  // First closing the adding note state
                  this.setState({ addingNote: false });

                  // then if we have the inputRef, change its value
                  if (this.notesInputRef?.current) {
                    this.notesInputRef.current.value = "";

                    // And then simulate the input event so the onChange gets triggered
                    const event = new Event("input", { bubbles: true });
                    this.notesInputRef.current.dispatchEvent(event);
                  }
                }}
                type="button"
              >
                <img
                  src={"/img/new-icons/red-trash.svg"}
                  width={16}
                  height={16}
                  alt="remove notes"
                />
                <span>{t("athlete-portal:programs.removeNote")}</span>
              </button>
            )}
          </div>
        </li>
      );
    }
    return null;
  }
}

EditorSessionTableRow.contextType = RillContext;

export default withTranslation(["athlete-portal", "common"])(
  EditorSessionTableRow
);
